import {
    checkReservationExists,
    checkWorker,
    getReservationsByTask,
    handleGetParticipantsByTask,
    isCallTask
} from "../../ActionUtils";
import { CbmImpl, ParticipantType } from "~/modules/cbm";
import { TaskParticipant } from "~/modules/participant/TaskParticipant";
import { Task } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { createFlexSdkError, throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

































export class CompleteTask implements Action<Promise<Task>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;

        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const worker = taskRouter.worker;

        checkWorker(worker, {
            actionName: "CompleteTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.CompleteTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const reservations = getReservationsByTask(worker, this.#taskSid).filter(
            (res) => res.status === "wrapping" || res.status === "accepted"
        );
        const reservation = reservations[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "CompleteTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.CompleteTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });
        const task = reservation.task;

        const { flexInteractionChannelSid: channelSid } = task.attributes;
        const isVoiceTask = isCallTask(task);

        if (channelSid && !isVoiceTask) {
            const participants = await handleGetParticipantsByTask(
                cbm,
                task,
                {
                    actionName: "CompleteTask",
                    moduleName: "actions/Task",
                    analytics,
                    context,
                    worker,
                    logger,
                    event: EVENTS.CompleteTaskCompleted,
                    extra: {
                        taskSid: this.#taskSid
                    }
                },
                { status: "all", taskSid: this.#taskSid }
            );

            const agent = participants.find(
                (participant: TaskParticipant) =>
                    participant.type === ParticipantType.Agent &&
                    participant.routingProperties?.workerSid === worker.sid
            );

            if (!agent?.participantSid) {
                const errorMsg = `CompleteTask: Failed to find agent participant for task ${this.#taskSid}`;
                logger.error(errorMsg);
                sendWorkerTrackingEvent(EVENTS.CompleteTaskCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid
                });
                throwFlexSdkError(context)(
                    ErrorCode.WorkerParticipantNotFoundForTask,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                );
            }

            const participantSid = agent!.participantSid;
            try {
                await cbm.removeParticipant(task, participantSid, {
                    closeChannelIfLastParticipant: true,
                    awaitRemovalConfirmation: false
                });
                return task;
            } catch (error) {
                const errorMsg = `CompleteTask: Failed to remove participant ${participantSid} from task ${
                    this.#taskSid
                }`;
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.CompleteTaskCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid
                });
                throwFlexSdkError(context)(
                    ErrorCode.FailedToRemoveParticipant,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                );
            }
        }

        try {
            await Promise.all(reservations.map((res) => cbm.completeReservation(res)));
            sendWorkerTrackingEvent(EVENTS.CompleteTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSids: reservations.map((res) => res.sid)
            });
        } catch (error) {
            const errorMsg = `CompleteTask: Failed to complete reservation for task ${this.#taskSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.CompleteTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSids: reservations.map((res) => res.sid)
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToCompleteReservation,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }

        return reservations[0].task;
    }
}
